import { z, isoZ } from 'my-zod'
import { dateFns, Iso, yearMonthFns } from 'iso-fns2'
import { enumOptionsToObject } from '../../internal/enum-helpers'
import _ from 'lodash'
import { getVehicleOrderDiscountSuggestions } from './get-suggested-discounts'
import { getVehicleOrderInvoice } from './get-vehicle-order-invoice'
import { getModelSnapshots } from './get-model-snapshots'
import { simpleDefaultZodErrorMap } from '../../internal/default-zod-error-map'

const vehicleOrderStageOptions = [
  'Draft', // In this stage the Sales Rep will put together a vehicle order, changing the details until they are satisfied all the details are correct.
  'Negotiating', // This stage indicates the Sales Rep is actively negotiating this vehicle order with the outfitter.
  'InternalReview', // The vehicle order owner can move the vehicle order from the Draft stage to Internal Review when they are ready for the Sales Manager to look over and approve the quote.
  'Approved', // Sales Manager can approve a quote.
  'OutForSignature', // After approval, the vehicle order owner or Sales Manager can change its status to Out for Signature.
  'Signed', // Vehicle Order was signed and signed copy uploaded. Fleet team and finance should be notified.
  'Active',
  'Archived',
  'Terminated',
  'Void'
] as const
const vehicleOrderStages = enumOptionsToObject(vehicleOrderStageOptions)
const vehicleOrderStageLabels: Record<(typeof vehicleOrderStageOptions)[number], string> = {
  Draft: 'Draft',
  Negotiating: 'Negotiating',
  InternalReview: 'Internal Review',
  Approved: 'Approved',
  OutForSignature: 'Out For Signature',
  Signed: 'Signed',
  Active: 'Active',
  Archived: 'Archived',
  Terminated: 'Terminated',
  Void: 'Void'
}

const vehicleOrderSeasonTypeOptions = [
  'Renewal',
  'SeasonExtension',
  'MidSeasonAdd',
  'FirstSeason',
  'ProductLineAdd',
  'SeasonHoldover'
] as const
const vehicleOrderSeasonTypes = enumOptionsToObject(vehicleOrderSeasonTypeOptions)
const vehicleOrderSeasonTypeLabels: Record<(typeof vehicleOrderSeasonTypeOptions)[number], string> = {
  Renewal: 'Renewal',
  SeasonExtension: 'Season Extension',
  MidSeasonAdd: 'Mid-Season Add',
  FirstSeason: 'First Season',
  ProductLineAdd: 'Product Line Add',
  SeasonHoldover: 'Season Holdover'
}

const vehicleOrderProgramTypeOptions = ['fleetSale', 'adventures', 'adventuresElite'] as const
const vehicleOrderProgramTypes = enumOptionsToObject(vehicleOrderProgramTypeOptions)
const vehicleOrderProgramTypeLabels: Record<(typeof vehicleOrderProgramTypeOptions)[number], string> = {
  fleetSale: 'Fleet Sale',
  adventures: 'Adventures',
  adventuresElite: 'Adventures Elite'
}

const vehicleOrderVehicleTypeOptions = ['SXS', 'IND', 'SLG', 'ATV', 'PON', 'SNO'] as const
const vehicleOrderVehicleTypes = enumOptionsToObject(vehicleOrderVehicleTypeOptions)
const vehicleOrderVehicleTypeLabels: Record<(typeof vehicleOrderVehicleTypeOptions)[number], string> = {
  SXS: 'SXS',
  IND: 'IND',
  SLG: 'SLG',
  ATV: 'ATV',
  PON: 'PON',
  SNO: 'SNO'
}

const vehicleOrderDiscountUnitOptions = ['percentage'] as const
const vehicleOrderDiscountUnits = enumOptionsToObject(vehicleOrderDiscountUnitOptions)
const vehicleOrderDiscountUnitLabels: Record<(typeof vehicleOrderDiscountUnitOptions)[number], string> = {
  percentage: 'Percentage'
}

const vehicleOrderDiscountTimingOptions = ['upfront', 'spreadEvenly'] as const
const vehicleOrderDiscountTimings = enumOptionsToObject(vehicleOrderDiscountTimingOptions)
const vehicleOrderDiscountTimingLabels: Record<(typeof vehicleOrderDiscountTimingOptions)[number], string> = {
  upfront: 'Upfront',
  spreadEvenly: 'Spread Evenly'
}

const vehicleOrderDiscountTypeOptions = ['volume', 'multiProductLine', 'existingOutfitter', 'elite', 'custom'] as const
const vehicleOrderDiscountTypes = enumOptionsToObject(vehicleOrderDiscountTypeOptions)
const vehicleOrderDiscountTypeLabels: Record<(typeof vehicleOrderDiscountTypeOptions)[number], string> = {
  volume: 'Volume',
  multiProductLine: 'Multi-line',
  existingOutfitter: 'Existing Outfitter',
  elite: 'Elite',
  custom: 'Custom'
}

const vehicleOrderAddonUnitOptions = ['fixed'] as const
const vehicleOrderAddonUnits = enumOptionsToObject(vehicleOrderAddonUnitOptions)
const vehicleOrderAddonUnitLabels: Record<(typeof vehicleOrderAddonUnitOptions)[number], string> = {
  fixed: 'Fixed'
}

const vehicleOrderAddonTimingOptions = ['upfront', 'spreadEvenly'] as const
const vehicleOrderAddonTimings = enumOptionsToObject(vehicleOrderAddonTimingOptions)
const vehicleOrderAddonTimingLabels: Record<(typeof vehicleOrderAddonTimingOptions)[number], string> = {
  upfront: 'Upfront',
  spreadEvenly: 'Spread Evenly'
}

const vehicleOrderVoidReasonOptions = [
  'externalRenegotiating',
  'externalDealLost',
  'internalNotApproved',
  'internalAdjustment',
  'internalNotAFit'
] as const
const vehicleOrderVoidReasons = enumOptionsToObject(vehicleOrderVoidReasonOptions)
const vehicleOrderVoidReasonLabels: Record<(typeof vehicleOrderVoidReasonOptions)[number], string> = {
  externalRenegotiating: 'External - Renegotiating',
  externalDealLost: 'External - Deal Lost',
  internalNotApproved: 'Internal - Not Approved',
  internalAdjustment: 'Internal - Adjustment',
  internalNotAFit: 'Internal - Not a fit'
}

const vehicleOrderConfidenceLevelOptions = ['0%', '25%', '50%', '75%', '100%'] as const
const vehicleOrderConfidenceLevels = enumOptionsToObject(vehicleOrderConfidenceLevelOptions)

const vehicleOrderPricingStrategyOptions = ['standard', 'custom'] as const
const vehicleOrderPricingStrategies = enumOptionsToObject(vehicleOrderPricingStrategyOptions)

const vehicleOrderDiscount = z.object({
  id: z.string().uuid(),
  type: z.enum(vehicleOrderDiscountTypeOptions),
  description: z.string().max(255).describe('Description of the discount'),
  amount: z.number().min(0).max(100).describe('Discount amount (fixed amount or percentage value)'),
  unit: z.enum(vehicleOrderDiscountUnitOptions).describe('Type of the discount applied to the vehicle order.'),
  timing: z.enum(vehicleOrderDiscountTimingOptions),
  billingCaseShortId: z
    .string()
    .min(1)
    .nullable()
    .describe('Short ID of the linked Billing Case required for custom discounts')
})

const vehicleOrderAddon = z
  .object({
    id: z.string().uuid(),
    description: z.string().max(255).describe('Description of the addon'),
    amount: z.number().describe('Addon amount (fixed amount or percentage value)'),
    unit: z.enum(vehicleOrderAddonUnitOptions).describe('Type of the addon applied to the vehicle order.'),
    timing: z.enum(vehicleOrderAddonTimingOptions),
    justification: z.string().nullable()
  })
  .superRefine((addon, ctx) => {
    if (addon.amount < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Fixed addon amount must be positive',
        path: ['amount']
      })
    }
  })

const vehicleOrderLineItemModel = z.object({
  modelNumber: z.string().describe('The model number for this line item (replaces modelFamilyId).'),
  quantity: z.number().min(1).describe('Quantity of vehicles for this model.'),
  priceOverride: z.number().nullable()
})

const vehicleOrderHoldingTermModel = z.object({
  id: z.string().uuid(),
  contractedStartDate: isoZ
    .date()
    .superRefine((value, ctx) => {
      // Holding term start date should always be either the 1st or 15th of the month
      if (dateFns.getDay(value) !== 1 && dateFns.getDay(value) !== 15) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Each holding term should start on either the 1st or 15th of the month.`,
          path: []
        })
      }
    })
    .nullable()
    .setLabel('Holding Term Start Date')
    .describe(
      'The date this holding term (when vehicle order signed) officially begins for purposes of billing and other contractual obligations. Should be the 1st or 15th of the month. Should not be within 90 days of the vehicle order date.'
    ),
  numberOfMonths: z
    .number()
    .min(1)
    .max(36)
    .nullable()
    .setLabel('Holding Term Length (Months)')
    .describe(
      'The number of months this holding term runs before ending for purposes of billing and other contractual obligations.'
    ),
  timelineJustification: z.string().nullable(),
  preferredVehicleDeliveryDate: isoZ
    .date()
    .describe('Preferred date for vehicles to arrive at outfitter shipping address.')
    .nullable(),
  earliestVehicleDeliveryDate: isoZ
    .date()
    .describe('Earliest possible date for vehicles to arrive at outfitter shipping address.'),
  preferredPickupDate: isoZ.date().nullable(),
  lineItems: vehicleOrderLineItemModel.array().min(1).describe('Must have at least one line item'),
  /** For leases, this is the invoice date (1st of month). For fleet sales, this is the payment due date. */
  paymentTerms: isoZ.date().array().min(1)
})

const vehicleOrderInvoiceLineModel = z.object({
  paymentTerm: isoZ.date(),
  addonId: z.string().nullable(),
  discountId: z.string().nullable(),
  holdingTermId: z.string().nullable(),
  modelNumber: z.string().nullable(),
  unitCost: z.number(),
  unitTaxAmount: z.number(),
  quantity: z.number()
})

const vehicleOrderModelSnapshotModel = z.object({
  modelNumber: z.string(),
  msrp: z.number(),
  freightCost: z.number(),
  year1ResidualValuePercentage: z.number().nullable(),
  year2ResidualValuePercentage: z.number().nullable(),
  year3ResidualValuePercentage: z.number().nullable()
})

const vehicleOrderHeadersModel = z.object({
  id: z.number(),
  shortId: z.string(),

  programType: z.enum(vehicleOrderProgramTypeOptions).describe('Type of the vehicle order.'),
  seasonType: z.enum(vehicleOrderSeasonTypeOptions, { errorMap: simpleDefaultZodErrorMap }),
  vehicleType: z
    .enum(vehicleOrderVehicleTypeOptions, { errorMap: simpleDefaultZodErrorMap })
    .describe('Type of vehicle being ordered.'),
  pricingStrategy: z.enum(vehicleOrderPricingStrategyOptions),

  stage: z.enum(vehicleOrderStageOptions).describe('Current stage of the vehicle order.'),
  voidReason: z.enum(vehicleOrderVoidReasonOptions).nullable(),
  ownerPolarisUserId: z
    .number()
    .nullable()
    .describe('The sales representative or account manager that owns this quote. Sales Managers can change this.'),
  primaryOutfitterId: z.number({ errorMap: simpleDefaultZodErrorMap }).describe('Outfitter ID for this order'),
  primaryOutfitterContactId: z.string({ errorMap: simpleDefaultZodErrorMap }),
  quoteDate: isoZ.date().setLabel('Vehicle Order Date'),
  expirationDate: isoZ.date()
    .describe(`Expiration date is the day that the vehicle order is no longer an offer. If not signed before this date, the vehicle order is void.
    Default is 15 days from vehicle order date.`),
  forecastedSignDate: isoZ.date().nullable(),
  nextSteps: z.string(),
  nextStepDate: isoZ.date().nullable(),
  confidenceLevel: z.enum(vehicleOrderConfidenceLevelOptions).nullable(),

  discounts: z.array(vehicleOrderDiscount),
  addons: z.array(vehicleOrderAddon),
  taxPercentage: z.number().describe('Tax percentage to apply to the order'),

  holdingTerms: z.array(vehicleOrderHoldingTermModel),

  signedPdfDocument: z
    .object({
      url: z.string().min(1),
      title: z.string().min(1),
      signedDate: isoZ.instant()
    })
    .nullable(),

  privateNotes: z.string().max(1024),
  sharedNotes: z.string().max(1024),

  /**
   * context
   */
  weightedAverageCostOfCapital: z.number(),
  additionalVehicleCosts: z.number().describe('Additional vehicle costs to include in financial calculations.'),
  modelSnapshots: vehicleOrderModelSnapshotModel.array(),
  invoiceLines: z.array(vehicleOrderInvoiceLineModel),
  createdDate: z.string().describe('Date when the vehicle order was created'),
  lastModifiedDate: z.string().describe('Date when the vehicle order was last updated'),
  deletedDate: z.string().nullable()
})

function getSalesQuoteHoldingTermInitDescription(holdingTerm: z.infer<typeof vehicleOrderHoldingTermModel>): string {
  const start = holdingTerm.contractedStartDate
    ? dateFns.toYearMonth(holdingTerm.contractedStartDate)
    : dateFns.toYearMonth(holdingTerm.preferredVehicleDeliveryDate ?? holdingTerm.earliestVehicleDeliveryDate)
  const end = holdingTerm.numberOfMonths ? yearMonthFns.add(start, { months: holdingTerm.numberOfMonths }) : null

  if (!end) {
    return `${yearMonthFns.format(start, 'MMM yyyy')}`
  } else {
    const sameYear = yearMonthFns.getYear(start) === yearMonthFns.getYear(end)
    if (sameYear) {
      return `${yearMonthFns.format(start, 'MMM')} - ${yearMonthFns.format(end, 'MMM yyyy')}`
    } else {
      return `${yearMonthFns.format(start, 'MMM yyyy')} - ${yearMonthFns.format(end, 'MMM yyyy')}`
    }
  }
}

function getVehicleOrderHoldingTermDescriptions<S extends z.infer<typeof vehicleOrderHoldingTermModel>>(
  holdingTerms: S[]
): (S & { description: string })[] {
  return Object.values(
    _.groupBy(
      holdingTerms
        .sort((s1, s2) => s1.id.localeCompare(s2.id))
        .map((s) => {
          const description = getSalesQuoteHoldingTermInitDescription(s)
          return { ...s, description }
        }),
      (s) => s.description
    )
  )
    .map((holdingTermGroup) =>
      holdingTermGroup.length > 1
        ? holdingTermGroup.map((holdingTerm, index) => ({
            ...holdingTerm,
            description: `${holdingTerm.description} (${index + 1})`
          }))
        : holdingTermGroup
    )
    .flat()
}

const vehicleOrderDiscountConfiguration = z.object({
  id: z.string().uuid(),
  shortId: z.string(),
  vehicleType: z.enum(vehicleOrderVehicleTypeOptions),
  amount: z.number().max(100),
  unit: z.enum(vehicleOrderDiscountUnitOptions),
  metadata: z.union([
    z.object({ type: z.literal('volume'), minimumUnitCount: z.number() }),
    z.object({ type: z.literal('multiProductLine') }),
    z.object({ type: z.literal('existingOutfitter') }),
    z.object({ type: z.literal('elite') })
  ]),
  createdDate: isoZ.instant(),
  lastModifiedDate: isoZ.instant(),
  deletedDate: isoZ.instant().nullable()
})

function getStartAndEndDatesForVehicleOrder(vehicleOrder: { holdingTerms: z.infer<typeof vehicleOrderHoldingTermModel>[] }) {
  const dates = vehicleOrder.holdingTerms.map((holdingTerm) => ({
    startDate:
      holdingTerm.contractedStartDate ?? holdingTerm.preferredVehicleDeliveryDate ?? holdingTerm.earliestVehicleDeliveryDate,
    endDate: holdingTerm.numberOfMonths
      ? dateFns.add(dateFns.from(holdingTerm.contractedStartDate), { months: holdingTerm.numberOfMonths })
      : null
  }))

  const earliestHoldingTermStart =
    dates.length > 0 ? dates.sort((a, b) => dateFns.compare(a.startDate, b.startDate))[0].startDate : null

  const latestHoldingTermEnd =
    dates.length > 0
      ? dates.sort((a, b) => (a.endDate && b.endDate ? dateFns.compare(b.endDate, a.endDate) : a.endDate ? 1 : -1))[0]
          .endDate
      : null

  return { startDate: earliestHoldingTermStart, endDate: latestHoldingTermEnd }
}

export const VehicleOrder2Schemas = {
  vehicleOrderStageOptions,
  vehicleOrderStages,
  vehicleOrderStageLabels,
  vehicleOrderProgramTypeOptions,
  vehicleOrderProgramTypes,
  vehicleOrderProgramTypeLabels,
  vehicleOrderSeasonTypeOptions,
  vehicleOrderSeasonTypes,
  vehicleOrderSeasonTypeLabels,
  vehicleOrderVehicleTypeOptions,
  vehicleOrderVehicleTypes,
  vehicleOrderVehicleTypeLabels,
  vehicleOrderDiscountUnitOptions,
  vehicleOrderDiscountUnits,
  vehicleOrderDiscountUnitLabels,
  vehicleOrderDiscountTimingOptions,
  vehicleOrderDiscountTimings,
  vehicleOrderDiscountTimingLabels,
  vehicleOrderDiscountTypes,
  vehicleOrderDiscountTypeLabels,
  vehicleOrderDiscountTypeOptions,
  vehicleOrderAddonUnitOptions,
  vehicleOrderAddonUnits,
  vehicleOrderAddonUnitLabels,
  vehicleOrderAddonTimingOptions,
  vehicleOrderAddonTimings,
  vehicleOrderAddonTimingLabels,
  vehicleOrderVoidReasonOptions,
  vehicleOrderVoidReasons,
  vehicleOrderVoidReasonLabels,
  vehicleOrderConfidenceLevelOptions,
  vehicleOrderConfidenceLevels,
  vehicleOrderPricingStrategyOptions,
  vehicleOrderPricingStrategies,
  vehicleOrderModelSnapshotModel,
  vehicleOrderHeadersModel,
  vehicleOrderHoldingTermModel,
  vehicleOrderLineItemModel,
  vehicleOrderInvoiceLineModel,
  vehicleOrderDiscount,
  vehicleOrderAddon,
  vehicleOrderDiscountConfiguration,

  getVehicleOrderInvoice,
  getVehicleOrderHoldingTermDescriptions,
  getVehicleOrderDiscountSuggestions,
  getModelSnapshots,
  getStartAndEndDatesForVehicleOrder
}

export declare namespace VehicleOrder2Schemas {
  export type vehicleOrderHeadersModel = z.infer<typeof vehicleOrderHeadersModel>
  export type vehicleOrderHoldingTerm = z.infer<typeof vehicleOrderHoldingTermModel>
  export type vehicleOrderLineItemModel = z.infer<typeof vehicleOrderLineItemModel>
  export type vehicleOrderDiscount = z.infer<typeof vehicleOrderDiscount>
  export type vehicleOrderAddon = z.infer<typeof vehicleOrderAddon>
  export type vehicleOrderModelSnapshot = z.infer<typeof vehicleOrderModelSnapshotModel>

  export type vehicleOrderDiscountConfiguration = z.infer<typeof vehicleOrderDiscountConfiguration>

  export type VehicleOrderStage = (typeof vehicleOrderStageOptions)[number]
  export type VehicleOrderProgramType = (typeof vehicleOrderProgramTypeOptions)[number]
  export type VehicleOrderSeasonType = (typeof vehicleOrderSeasonTypeOptions)[number]
  export type VehicleOrderVehicleType = (typeof vehicleOrderVehicleTypeOptions)[number]
  export type VehicleOrderDiscountUnit = (typeof vehicleOrderDiscountUnitOptions)[number]
  export type VehicleOrderDiscountType = (typeof vehicleOrderDiscountTypeOptions)[number]
  export type VehicleOrderPricingStrategy = (typeof vehicleOrderPricingStrategyOptions)[number]

  export type VehicleOrderAddonUnit = (typeof vehicleOrderAddonUnitOptions)[number]
  export type VehicleOrderVoidReason = (typeof vehicleOrderVoidReasonOptions)[number]
  export type VehicleOrderConfidenceLevel = (typeof vehicleOrderConfidenceLevelOptions)[number]

  export type VehicleOrderInvoiceLine = z.infer<typeof vehicleOrderInvoiceLineModel>

  export type VehicleOrderInvoice = {
    vehicles: {
      holdingTerm: vehicleOrderHoldingTerm
      modelNumber: string
      quantity: number
      paymentTerms: {
        paymentTerm: Iso.Date
        amount: number
      }[]
      subtotal: number
      taxAmount: number
    }[]
    addons: {
      addon: vehicleOrderAddon
      paymentTerms: {
        paymentTerm: Iso.Date
        amount: number
      }[]
      total: number
    }[]
    discounts: {
      discount: vehicleOrderDiscount
      paymentTerms: {
        paymentTerm: Iso.Date
        amount: number
      }[]
      total: number
    }[]
    subtotal: number
    taxAmount: number
    total: number
    payments: {
      paymentTerm: Iso.Date
      total: number
      taxAmount: number
      subtotal: number
      discounts: { discount: vehicleOrderDiscount; amount: number }[]
      addons: { addon: vehicleOrderAddon; amount: number }[]
      vehicles: { modelNumber: string; quantity: number; holdingTerm: vehicleOrderHoldingTerm; amount: number }[]
    }[]
  }
}
